import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { FaHeart, FaCopy, FaShare, FaSignOutAlt, FaEye, FaEnvelope, FaInfoCircle, FaTrash, FaTwitter, FaFacebook, FaWhatsapp, FaShareAlt, FaDownload, FaInstagram, FaTimes, FaMapMarkerAlt, FaUser, FaCrown, FaClock } from 'react-icons/fa';
import html2canvas from 'html2canvas';
import { useToast } from '../context/ToastContext';
import UpgradeModal from '../components/UpgradeModal';
import { useNavigate } from 'react-router-dom';
import HeartLoader from '../components/HeartLoader';
import Footer from '../components/Footer';
import { isBengaliText } from '../utils/textUtils';
import InstallPrompt from '../components/InstallPrompt';

const ShareableMessage = ({ message, onClose }) => {
  const messageRef = useRef();
  const [generating, setGenerating] = useState(false);
  const { showToast } = useToast();
  const [imageUrl, setImageUrl] = useState(null);

  useEffect(() => {
    generateImage();
  }, []);

  const generateImage = async () => {
    try {
      setGenerating(true);
      const element = messageRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: null,
        removeContainer: true,
        logging: false,
        useCORS: true,
        allowTaint: true,
      });
      
      const url = canvas.toDataURL('image/png');
      setImageUrl(url);
    } catch (error) {
      console.error('Error generating image:', error);
      showToast('Failed to generate image', 'error');
    } finally {
      setGenerating(false);
    }
  };

  const handleDownload = () => {
    if (!imageUrl) return;
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = 'chithi.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleShare = async (platform) => {
    if (!imageUrl) return;
    
    try {
      const blob = await (await fetch(imageUrl)).blob();
      const file = new File([blob], 'anonymous-message.png', { type: 'image/png' });
      
      // For native sharing (mobile devices)
      if (platform === 'native' && navigator.share) {
        await navigator.share({
          files: [file],
          title: 'Anonymous Message',
          text: 'Check out this anonymous message!'
        });
        showToast('Shared successfully!', 'success');
        return;
      }

      // Deep linking for social apps
      const shareUrls = {
        twitter: `twitter://post?message=${encodeURIComponent('Check out this anonymous message!')}`,
        facebook: `fb://composer?image=${encodeURIComponent(imageUrl)}`,
        whatsapp: `whatsapp://send?text=${encodeURIComponent('Check out this anonymous message!')}`,
        instagram: `instagram://library?AssetPath=${encodeURIComponent(imageUrl)}`,
      };

      const webFallbacks = {
        twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent('Check out this anonymous message!')}`,
        facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(window.location.href)}`,
        whatsapp: `https://wa.me/?text=${encodeURIComponent('Check out this anonymous message!')}`,
        instagram: `https://instagram.com`,
      };

      // Try deep linking first, fallback to web
      const tryDeepLink = (url, fallback) => {
        const timeout = setTimeout(() => {
          window.location.href = fallback;
        }, 2500);

        window.location.href = url;

        window.addEventListener('blur', () => {
          clearTimeout(timeout);
        });
      };

      if (platform && shareUrls[platform]) {
        tryDeepLink(shareUrls[platform], webFallbacks[platform]);
        showToast(`Opening ${platform}...`, 'info');
      }
    } catch (error) {
      console.error('Error sharing:', error);
      showToast('Failed to share. Try another method.', 'error');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl w-full max-w-sm mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b">
          <h3 className="text-lg font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
            Share Your Message
          </h3>
          <button
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-full hover:bg-gray-100"
          >
            <FaTimes className="w-5 h-5" />
          </button>
        </div>

        {/* Message Preview */}
        <div className="p-4">
          <div 
            ref={messageRef}
            className="relative bg-gradient-to-br from-purple-600 to-pink-600 rounded-lg overflow-hidden p-6 mb-4 mx-auto"
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
            
            <div className="relative z-10">
              <p className={`${
                isBengaliText(message.content) 
                  ? 'font-bengaliHandwriting text-xl' 
                  : 'font-vicky text-xl'
              } text-white break-words whitespace-pre-wrap text-center`}>
                {message.content}
              </p>
              <div className="absolute -bottom-2 -right-2">
                <FaHeart className="text-white/50 w-4 h-4" />
              </div>
            </div>
          </div>
        </div>

        {/* Share Buttons */}
        <div className="p-4">
          <div className="grid grid-cols-6 gap-3 max-w-xs mx-auto">
            <button
              onClick={() => handleShare('twitter')}
              className="aspect-square flex items-center justify-center bg-[#1DA1F2] text-white rounded-full hover:opacity-90 transition-all"
              title="Share on Twitter"
            >
              <FaTwitter className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleShare('facebook')}
              className="aspect-square flex items-center justify-center bg-[#4267B2] text-white rounded-full hover:opacity-90 transition-all"
              title="Share on Facebook"
            >
              <FaFacebook className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleShare('whatsapp')}
              className="aspect-square flex items-center justify-center bg-[#25D366] text-white rounded-full hover:opacity-90 transition-all"
              title="Share on WhatsApp"
            >
              <FaWhatsapp className="w-5 h-5" />
            </button>
            <button
              onClick={() => handleShare('instagram')}
              className="aspect-square flex items-center justify-center bg-gradient-to-br from-[#833AB4] via-[#FD1D1D] to-[#FCAF45] text-white rounded-full hover:opacity-90 transition-all"
              title="Share on Instagram"
            >
              <FaInstagram className="w-5 h-5" />
            </button>
            <button
              onClick={handleDownload}
              disabled={generating || !imageUrl}
              className="aspect-square flex items-center justify-center bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-full hover:opacity-90 transition-all disabled:opacity-50"
              title="Download Image"
            >
              {generating ? (
                <span className="animate-spin">⏳</span>
              ) : (
                <FaDownload className="w-5 h-5" />
              )}
            </button>
            {navigator.share && (
              <button
                onClick={() => handleShare('native')}
                className="aspect-square flex items-center justify-center bg-purple-600 text-white rounded-full hover:opacity-90 transition-all"
                title="Share using device options"
              >
                <FaShareAlt className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const MessageCard = ({ message, onReveal, onDelete }) => {
  const [isRevealed, setIsRevealed] = useState(message.isRevealed);
  const [showInfo, setShowInfo] = useState(false);
  const [showShareable, setShowShareable] = useState(false);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { showToast } = useToast();
  const { user, updatePremiumStatus } = useAuth();
  const isPremium = user?.isPremium;

  useEffect(() => {
    if (!isPremium && showInfo) {
      setShowInfo(false);
    }
  }, [isPremium, showInfo]);

  const handleInfoClick = () => {
    if (isPremium) {
      setShowInfo(!showInfo);
    } else {
      setShowUpgradeModal(true);
      showToast('This feature is available for premium users only', 'info');
    }
  };

  const handleReveal = async () => {
    if (isRevealed) return;
    
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/messages/${message._id}/reveal`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.ok) {
        setIsRevealed(true);
        onReveal(message._id);
      }
    } catch (error) {
      console.error('Failed to reveal message:', error);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/messages/${message._id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`
          }
        });

        if (response.ok) {
          onDelete(message._id);
        }
      } catch (error) {
        console.error('Failed to delete message:', error);
      }
    }
  };

  return (
    <>
      <div className="h-[300px]">
            {isRevealed ? (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 h-full border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)]">
                <div className="relative h-full flex flex-col">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
                  
              <div className="relative flex flex-col h-full z-10">
                      <div className="absolute -top-2 -right-2 flex gap-1">
                        <button
                          onClick={handleInfoClick}
                    className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                          title={isPremium ? "Show Sender Info" : "Premium Feature"}
                        >
                          <FaInfoCircle className="w-4 h-4" />
                  </button>
                  <button
                          onClick={() => setShowShareable(true)}
                    className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                          title="Share as Image"
                        >
                          <FaShareAlt className="w-4 h-4" />
                        </button>
                        <button
                          onClick={handleDelete}
                    className="p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                          title="Delete Message"
                        >
                          <FaTrash className="w-4 h-4" />
                        </button>
                      </div>

                <div className={`flex-1 mt-6 ${showInfo ? 'max-h-[120px] overflow-y-auto scrollbar-thin scrollbar-thumb-white/20 scrollbar-track-transparent' : ''}`}>
                        <p className={`${
                          isBengaliText(message.content) 
                            ? `font-bengaliHandwriting ${showInfo ? 'text-lg' : 'text-xl'}`
                            : `font-vicky ${showInfo ? 'text-xl' : 'text-2xl'}`
                  } leading-relaxed break-words whitespace-pre-wrap`}>
                          {message.content}
                        </p>
                      </div>

                      {isPremium && showInfo && (
                  <div className="border-t border-white/10 pt-3 mt-3 animate-fadeIn">
                    <div className="bg-white/10 backdrop-blur-sm rounded-lg p-3">
                      <h4 className="text-xs font-semibold mb-1">Sender Info:</h4>
                      <div className="space-y-0.5 text-xs text-white/80">
                              <p className="truncate">IP: {message.metadata.ipAddress}</p>
                              <p className="truncate">Device: {message.metadata.device}</p>
                              <p className="truncate">Browser: {message.metadata.browser}</p>
                              <p className="truncate flex items-center gap-1">
                                Location: {message.metadata.location}
                                {message.metadata.maplink && (
                                  <a 
                                    href={message.metadata.maplink}
                                    target="_blank"
                                    rel="noopener noreferrer"
                              className="text-white hover:text-white/80 transition-colors"
                                    title="View on map"
                                  >
                                    <FaMapMarkerAlt className="w-3 h-3" />
                                  </a>
                                )}
                              </p>
                              <p className="truncate">Date & Time: {new Date(message.metadata.timestamp).toLocaleString()}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
        ) : (
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 h-full border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)]">
            <div className="relative h-full flex items-center justify-center">
              <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
              <div className="absolute bottom-0 left-0 w-24 h-24 bg-black/10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
              
              <div className="relative text-center z-10">
                <div className="w-16 h-16 mx-auto mb-4 bg-white/20 rounded-full flex items-center justify-center">
                  <FaEnvelope className="w-8 h-8" />
                  </div>
                <p className="text-white/80 mb-2">Anonymous Message</p>
                <p className="text-sm text-white/60 mb-4">
                      {new Date(message.createdAt).toLocaleDateString()}
                    </p>
                    <button
                      onClick={handleReveal}
                  className="px-6 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center mx-auto"
                    >
                      <FaEye className="mr-2 w-4 h-4" />
                      Click to Reveal
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>

      {showShareable && (
        <ShareableMessage 
          message={message} 
          onClose={() => setShowShareable(false)} 
        />
      )}
      {showUpgradeModal && (
        <UpgradeModal 
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={updatePremiumStatus}
        />
      )}
    </>
  );
};

const Dashboard = () => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const { user, logout, updatePremiumStatus } = useAuth();
  const { showToast } = useToast();
  const navigate = useNavigate();
  const isInitialCheck = useRef(true);

  useEffect(() => {
    if (user?.isAdmin) {
      navigate('/admin');
    }
  }, [user, navigate]);

  useEffect(() => {
    // Initial data fetch and premium check
    fetchMessages();
    checkPremiumStatus();

    // Set up polling for premium status
    const interval = setInterval(checkPremiumStatus, 30000);

    // Cleanup on unmount
    return () => clearInterval(interval);
  }, []);

  const checkPremiumStatus = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/users/me/premium-status`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch premium status');
      }

      const data = await response.json();
      
      // Update local storage and context
      const storedUser = JSON.parse(localStorage.getItem('user'));
      const updatedUser = {
        ...storedUser,
        isPremium: data.isPremium,
        premiumExpiry: data.premiumExpiry
      };
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Only show status change notifications after the initial check
      if (!isInitialCheck.current && storedUser.isPremium !== data.isPremium) {
        showToast(
          data.isPremium ? 'Premium activated!' : 'Premium status expired',
          data.isPremium ? 'success' : 'info'
        );
      }

      // Update the auth context
      updatePremiumStatus(data.premiumExpiry, data.isPremium);
      
      // Set initial check to false after first run
      isInitialCheck.current = false;
    } catch (error) {
      console.error('Error checking premium status:', error);
    }
  };

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/messages`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch messages');
      }

      const data = await response.json();
      const processedData = data.map(msg => ({
        ...msg,
        isRevealed: Boolean(msg.isRevealed)
      }));
      setMessages(processedData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const copyMessageLink = () => {
    const link = `${window.location.origin}/${user.username}`;
    navigator.clipboard.writeText(link);
    showToast('Link copied to clipboard!', 'success');
  };

  const shareMessageLink = async () => {
    const link = `${window.location.origin}/${user.username}`;
    try {
      await navigator.share({
        title: 'Send me anonymous messages',
        text: 'Send me anonymous messages through this link:',
        url: link,
      });
      showToast('Link shared successfully!', 'success');
    } catch (err) {
      if (err.name !== 'AbortError') {
        copyMessageLink();
      }
    }
  };

  const handleMessageReveal = (messageId) => {
    setMessages(messages.map(msg => 
      msg._id === messageId ? { ...msg, isRevealed: true } : msg
    ));
  };

  const handleDeleteMessage = (messageId) => {
    setMessages(messages.filter(msg => msg._id !== messageId));
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-pink-100 via-purple-100 to-indigo-100 flex items-center justify-center">
        <HeartLoader size="large" />
      </div>
    );
  }

  return (
    <>
      <div className="min-h-screen relative bg-gradient-to-br from-purple-600 to-pink-600 flex flex-col text-white">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-white/10 rounded-full transform translate-x-1/3 -translate-y-1/3"></div>
          <div className="absolute bottom-0 left-0 w-[600px] h-[600px] bg-black/10 rounded-full transform -translate-x-1/3 translate-y-1/3"></div>
          
          {[...Array(20)].map((_, i) => (
            <div
              key={i}
              className="absolute animate-fall"
              style={{
                left: `${Math.random() * 100}%`,
                top: `-50px`,
                animationDelay: `${Math.random() * 10}s`,
                animationDuration: `${Math.random() * 5 + 8}s`,
                opacity: 0.3,
              }}
            >
              <FaHeart
                className="text-white animate-heart-spin"
                style={{
                  fontSize: `${Math.random() * 15 + 10}px`,
                }}
              />
            </div>
          ))}
          
          <div className="absolute top-1/4 right-1/4 w-64 h-64 bg-white/5 rounded-full blur-xl animate-float-slow"></div>
          <div className="absolute bottom-1/3 left-1/3 w-48 h-48 bg-white/5 rounded-full blur-xl animate-float-delayed"></div>
        </div>

        <div className="relative flex-1 flex flex-col z-10">
          <div className="border-b border-white/10">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8">
                <div className="flex items-center py-4">
                  <div className="bg-white/20 rounded-full p-2.5">
                    <FaUser className="w-5 h-5" />
                  </div>
                  <div className="ml-3">
                    <h1 className="text-lg font-semibold">
                      {user.username}
                    </h1>
                    <p className="text-sm text-white/80">
                      {user.isPremium ? (
                        <span className="flex items-center">
                          <FaCrown className="w-3.5 h-3.5 mr-1 text-yellow-300" />
                          Premium Member
                        </span>
                      ) : (
                        'Free Member'
                      )}
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  {!user.isPremium && (
                    <button
                      onClick={() => setShowUpgradeModal(true)}
                      className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center"
                    >
                      <FaCrown className="w-4 h-4 mr-2 text-yellow-300" />
                      <span className="text-sm">Upgrade</span>
                    </button>
                  )}
                  <button
                    onClick={logout}
                    className="p-2.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors flex items-center"
                    title="Logout"
                  >
                    <FaSignOutAlt className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>

            <br />

            <div className="border-t border-white/10">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col sm:flex-row items-center justify-around py-2 text-sm gap-4 sm:gap-8">
                  <div className="flex items-center space-x-8">
                    <div className="flex items-center">
                      <FaEnvelope className="w-4 h-4 mr-2 text-white/70" />
                      <span className="text-white/70">Messages: </span>
                      <span className="ml-1 font-semibold">{messages.length}</span>
                    </div>
                    <div className="flex items-center">
                      <FaEye className="w-4 h-4 mr-2 text-white/70" />
                      <span className="text-white/70">Revealed: </span>
                      <span className="ml-1 font-semibold">
                        {messages.filter(m => m.isRevealed).length}
                      </span>
                    </div>
                  </div>
                  {user.isPremium && (
                    <div className="flex items-center">
                      <FaClock className="w-4 h-4 mr-2 text-white/70" />
                      <span className="text-white/70">Premium Expires: </span>
                      <span className="ml-1 font-semibold">
                        {new Date(user.premiumExpiry).toLocaleDateString()}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
              <div className="mb-8">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4 sm:p-6 border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)] overflow-hidden">
                  <div className="absolute top-0 right-0 w-64 h-64 bg-white/10 rounded-full transform translate-x-1/4 -translate-y-1/4"></div>
                  <div className="absolute bottom-0 left-0 w-48 h-48 bg-black/10 rounded-full transform -translate-x-1/4 translate-y-1/4"></div>
                  
                  <div className="relative flex flex-col md:flex-row items-center justify-between gap-3 sm:gap-6">
                    <div className="flex-1 w-full">
                      <h2 className="text-lg sm:text-xl font-bold mb-1 sm:mb-2">Share Your Link</h2>
                      <p className="text-white/80 text-xs sm:text-sm mb-3 sm:mb-4">
                        Let others send you anonymous messages
                      </p>
                      <div className="flex flex-col sm:flex-row items-center gap-3">
                        <div className="w-full flex items-center gap-2 bg-white/10 border border-white/10 p-1.5 sm:p-2 rounded-lg backdrop-blur-sm">
                          <input
                            type="text"
                            readOnly
                            value={`${window.location.origin}/${user.username}`}
                            className="flex-1 bg-transparent border-none text-white placeholder-white/60 focus:outline-none focus:ring-0 text-xs sm:text-sm truncate min-w-0"
                          />
                          <div className="flex gap-1.5 shrink-0">
                            <button
                              onClick={copyMessageLink}
                              className="p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                              title="Copy Link"
                            >
                              <FaCopy className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </button>
                            {navigator.share && (
                              <button
                                onClick={shareMessageLink}
                                className="p-1.5 sm:p-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                                title="Share Link"
                              >
                                <FaShare className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                              </button>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="hidden md:block shrink-0">
                      <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center">
                        <FaEnvelope className="w-8 h-8 sm:w-10 sm:h-10" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {messages.length === 0 ? (
                  <div className="col-span-full">
                    <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 text-center border border-white/20 shadow-[0_0_15px_rgba(255,255,255,0.07)]">
                      <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-4">
                        <FaHeart className="w-8 h-8" />
                      </div>
                      <h3 className="text-xl font-bold mb-2">
                        No Messages Yet
                      </h3>
                      <p className="text-white/80">Share your link to start receiving anonymous messages!</p>
                    </div>
                  </div>
                ) : (
                  messages.map((message) => (
                    <MessageCard 
                      key={message._id} 
                      message={message} 
                      onReveal={handleMessageReveal}
                      onDelete={handleDeleteMessage}
                    />
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-8 border-t border-white/10">
          <Footer variant="dark" />
        </div>
      </div>
      
      <InstallPrompt />
      
      {showUpgradeModal && (
        <UpgradeModal 
          onClose={() => setShowUpgradeModal(false)}
          onUpgrade={updatePremiumStatus}
        />
      )}
    </>
  );
};

export default Dashboard; 